// SCR-STAFF-903 (AP-PLT-05): پروانه بهره‌برداری و مسئول فنی شعبه (US-PLT-011؛ هشدار ۶۰/۳۰/۷ روز و انقضا) و غیرفعال/فعال‌سازی
// شعبه با پیش‌نمایش اثر (US-PLT-013): آخرین شعبه فعال (AC-PLT-013.2) یا موارد باز با تعداد (AC-PLT-013.1) عملیات را مسدود می‌کند.
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import { unwrap, type BranchView, type WorkloadItem } from '@phoenix/api-client';
import { useAccess, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  ErrorState,
  faDigits,
  FormDialog,
  JalaliDatePickerRHF,
  LoadingState,
  SelectRHF,
  TextFieldRHF,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { useForm } from 'react-hook-form';
import { pltKeys, toCount } from './common';

/** رنگ نشان هشدار پروانه (BranchLicenseView.warning: none، expiring ≤۶۰، soon ≤۳۰، critical ≤۷، expired). */
export function LicenseChip({ branch }: { branch: BranchView }) {
  const { t } = useTranslation('plt');
  const license = branch.license;
  if (!license.licenseNumber && !license.licenseExpiresOn) {
    return <span>{t('plt:license.none')}</span>;
  }
  const days = license.daysLeft === null || license.daysLeft === undefined ? null : toCount(license.daysLeft);
  const color =
    license.warning === 'expired' || license.warning === 'critical'
      ? 'error'
      : license.warning === 'soon' || license.warning === 'expiring'
        ? 'warning'
        : 'success';
  return (
    <Chip
      size="small"
      color={color}
      variant={license.warning === 'none' ? 'outlined' : 'filled'}
      data-testid={`license-${branch.code}`}
      data-warning={license.warning}
      label={
        license.warning === 'expired'
          ? t('plt:license.expired')
          : days === null
            ? t('plt:license.registered')
            : t('plt:license.days_left', { days: faDigits(days) })
      }
    />
  );
}

interface LicenseForm {
  licenseNumber: string;
  licenseExpiresOn: string | null;
  technicalDirectorProviderId: string;
  technicalDirectorCouncilNumber: string;
}

/** ارائه‌دهندگان فعال برای انتخاب مسئول فنی (AC-PLT-011.2؛ فقط با iam.member.view). */
function useActiveProviders() {
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const enabled = access.can('iam.member.view');
  const query = useQuery({
    queryKey: pltKeys.providers(session?.tenantId),
    queryFn: () =>
      unwrap(tenantApi.GET('/api/v1/identity/providers', { params: { query: { status: 'Active' } } })),
    enabled,
  });
  return { providers: query.data?.items ?? [], enabled };
}

export function BranchLicenseDialog({ branch, onClose }: { branch: BranchView; onClose: () => void }) {
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const { providers, enabled } = useActiveProviders();
  const license = branch.license;
  const [fieldError, setFieldError] = useState(false);
  const { control, handleSubmit, formState, setError } = useForm<LicenseForm>({
    defaultValues: {
      licenseNumber: license.licenseNumber ?? '',
      licenseExpiresOn: license.licenseExpiresOn ?? null,
      technicalDirectorProviderId: license.technicalDirectorProviderId ?? '',
      technicalDirectorCouncilNumber: license.technicalDirectorCouncilNumber ?? '',
    },
  });

  const save = useMutation({
    mutationFn: (form: LicenseForm) =>
      unwrap(
        tenantApi.PUT('/api/v1/platform/branches/{id}/license', {
          params: { path: { id: branch.id } },
          body: {
            licenseNumber: form.licenseNumber.trim() || null,
            licenseExpiresOn: form.licenseExpiresOn,
            technicalDirectorProviderId: form.technicalDirectorProviderId || null,
            technicalDirectorCouncilNumber: form.technicalDirectorCouncilNumber.trim() || null,
            rowVersion: branch.rowVersion,
          },
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: pltKeys.branches(session?.tenantId) });
      onClose();
    },
    onMutate: () => setFieldError(false),
    onError: (error) =>
      setFieldError(
        applyServerErrors(error, setError, {
          fields: [
            'licenseNumber',
            'licenseExpiresOn',
            'technicalDirectorProviderId',
            'technicalDirectorCouncilNumber',
          ],
          codeFields: { 'PHX-PLT-0307': 'technicalDirectorProviderId' },
        }) > 0,
      ),
  });

  const options = [
    { value: '', label: '—' },
    ...providers.map((provider) => ({ value: provider.providerId, label: provider.displayName })),
  ];
  if (
    license.technicalDirectorProviderId &&
    !providers.some((provider) => provider.providerId === license.technicalDirectorProviderId)
  ) {
    options.push({
      value: license.technicalDirectorProviderId,
      label: license.technicalDirectorName ?? license.technicalDirectorProviderId,
    });
  }

  return (
    <FormDialog
      open
      titleKey="plt:license.title"
      screenId="SCR-STAFF-903"
      onSubmit={() => void handleSubmit((form) => save.mutate(form))()}
      onClose={onClose}
      isDirty={formState.isDirty}
      isSubmitting={save.isPending}
      submitError={fieldError ? undefined : (save.error ?? undefined)}
    >
      <TextFieldRHF
        control={control}
        name="licenseNumber"
        labelKey="plt:license.number"
        fullWidth
        slotProps={{ htmlInput: { dir: 'ltr', maxLength: 64, 'data-testid': 'license-number' } }}
      />
      <JalaliDatePickerRHF control={control} name="licenseExpiresOn" labelKey="plt:license.expires_on" />
      <SelectRHF
        control={control}
        name="technicalDirectorProviderId"
        labelKey="plt:license.technical_director"
        helperKey={
          enabled ? 'plt:license.technical_director_help' : 'plt:license.technical_director_no_access'
        }
        fullWidth
        disabled={!enabled}
        options={options}
      />
      <TextFieldRHF
        control={control}
        name="technicalDirectorCouncilNumber"
        labelKey="plt:license.council_number"
        fullWidth
        slotProps={{ htmlInput: { dir: 'ltr', maxLength: 32 } }}
      />
    </FormDialog>
  );
}

function ImpactList({ items }: { items: readonly WorkloadItem[] }) {
  const { t } = useTranslation('plt');
  return (
    <List dense data-testid="open-items">
      {items.map((item) => (
        <ListItem
          key={`${item.module}.${item.kindKey}`}
          data-testid={`open-item-${item.kindKey.replace(/^.*\./, '')}`}
        >
          <ListItemText
            primary={t(item.kindKey, { defaultValue: item.kindKey })}
            secondary={t('plt:impact.count', {
              count: toCount(item.count),
              value: faDigits(toCount(item.count)),
            })}
          />
        </ListItem>
      ))}
    </List>
  );
}

export { ImpactList };

/** غیرفعال‌سازی (با پیش‌نمایش اثر) یا فعال‌سازی دوباره شعبه. */
export function BranchStatusDialog({ branch, onClose }: { branch: BranchView; onClose: () => void }) {
  const { t } = useTranslation('plt');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const titleId = useId();
  const deactivating = branch.status === 'Active';

  const preview = useQuery({
    queryKey: ['platform', 'branches', branch.id, 'deactivation-preview'],
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/platform/branches/{id}/deactivation-preview', {
          params: { path: { id: branch.id } },
        }),
      ),
    enabled: deactivating,
    gcTime: 0,
  });

  const change = useMutation({
    mutationFn: () =>
      deactivating
        ? unwrap(
            tenantApi.POST('/api/v1/platform/branches/{id}:deactivate', {
              params: { path: { id: branch.id } },
            }),
          )
        : unwrap(
            tenantApi.POST('/api/v1/platform/branches/{id}:activate', {
              params: { path: { id: branch.id } },
            }),
          ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: pltKeys.branches(session?.tenantId) });
      onClose();
    },
  });

  const data = preview.data;
  const blocked = deactivating && (!data || !data.canDeactivate);
  return (
    <Dialog
      open
      onClose={change.isPending ? undefined : onClose}
      aria-labelledby={titleId}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id={titleId}>
        {t(deactivating ? 'plt:branch_status.deactivate_title' : 'plt:branch_status.activate_title', {
          name: branch.name,
        })}
      </DialogTitle>
      <DialogContent>
        {deactivating ? (
          preview.isPending ? (
            <LoadingState rows={2} />
          ) : preview.error ? (
            <ErrorState error={preview.error} compact />
          ) : data?.isLastActive ? (
            <Alert severity="error" data-testid="branch-last-active">
              {t('plt:branch_status.last_active')}
            </Alert>
          ) : data && data.items.length > 0 ? (
            <Alert severity="warning" data-testid="branch-open-items">
              <AlertTitle>{t('plt:branch_status.open_items')}</AlertTitle>
              <ImpactList items={data.items} />
            </Alert>
          ) : (
            <DialogContentText>{t('plt:branch_status.deactivate_message')}</DialogContentText>
          )
        ) : (
          <DialogContentText>{t('plt:branch_status.activate_message')}</DialogContentText>
        )}
        {change.error ? <ErrorState error={change.error} compact /> : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={change.isPending}>
          {t('common:actions.cancel')}
        </Button>
        <Button
          variant="contained"
          color={deactivating ? 'error' : 'primary'}
          onClick={() => change.mutate()}
          disabled={blocked || change.isPending}
          data-testid="branch-status-confirm"
        >
          {t(deactivating ? 'plt:branch_status.deactivate' : 'plt:branch_status.activate')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
